import { NumberField, CheckboxField, CheckboxGroup } from '@flos/react-ui';
import {
    AmountOrUnknown as AmountOrUnknownType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../../state/appState';
import React, { useMemo, useState } from 'react';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';

import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getValue } from '../values/Get';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const AmountOrUnknown = ({ caption, elementId, placeholder, checkboxLabel, helpText }: AmountOrUnknownType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = useMemo(() => formValues[elementId] ?? { isChecked: false, fieldValue: '' }, [formValues, elementId]);

    const [fieldValueIsValid, setFieldValueIsValid] = useState<boolean | undefined>(undefined);

    const onChangeHandler = (changedValue: string) => {
        const parsedValue = changedValue.length > 0 ? changedValue : '';
        setFormValue(elementId, { ...value, isChecked: false, fieldValue: parsedValue });
    };

    const onCheckHandler = (isChecked: boolean) => {
        setFormValue(elementId, { ...value, isChecked });
        setFieldValueIsValid(isChecked ? undefined : value.fieldValue !== '' ? true : undefined);
    };

    const onBlurHandler = (numberValue: string) => {
        const isValid = numberValue !== '' || value.isChecked === true;
        setFieldValueIsValid(isValid);
    };

    const isRequiredField = isRequired(formConfig, elementId);

    return (
        <>
            {caption && <h6>{caption}</h6>}

            <div className={helpText && 'u-spacing-stack-l'}>
                <NumberField
                    value={value.fieldValue}
                    onValueChange={(values) => {
                        const value = values.formattedValue;
                        onChangeHandler(value);
                    }}
                    onBlur={() => onBlurHandler(value.fieldValue)}
                    isValid={fieldValueIsValid}
                    errorText={'Feltet skal udfyldes'}
                    id={elementId}
                    key={elementId}
                    helpText={helpText}
                    label={placeholder}
                    withFormatting
                    decimalsAllowed={2}
                    required={isRequiredField}
                    aria-label={caption}
                    data-testid="amountfield"
                    inputMode="decimal"
                />
            </div>
            <div className="clear-fix u-spacing-stack-m"></div>
            <CheckboxGroup required={false} id={`${elementId}-checkboxgroup`} isValid={true}>
                <CheckboxField
                    id={`${elementId}-checkbox`}
                    key="dontKnowValue"
                    label={checkboxLabel}
                    value="dontKnowValue"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const isChecked = event.target.checked;
                        onCheckHandler(isChecked);
                    }}
                    checked={value.isChecked}
                    disabled={value.fieldValue.length !== 0}
                />
            </CheckboxGroup>
        </>
    );
};

AmountOrUnknown.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as AmountOrUnknownType;
    const { checkboxLabel, summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return (
        <SummaryRowTemplate
            elementId={elementId}
            caption={summaryCaption}
            value={value.isChecked === false ? `${value.fieldValue} kr.` : checkboxLabel}
        />
    );
};

AmountOrUnknown.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const valueObj = AmountOrUnknown.getValue(formValues, elementId);
    const valueStr = AmountOrUnknown.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('amountOrUnknown', elementId, label, valueStr, valueId);
};

AmountOrUnknown.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

AmountOrUnknown.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = AmountOrUnknown.getValue(formValues, elementId);
    if (value === undefined) {
        return { isChecked: false, fieldValue: '' };
    }
    return value;
};

AmountOrUnknown.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = AmountOrUnknown.getValue(formValues, elementId);

    const valueStr = value.isChecked ? 'Jeg kender ikke beløbet' : `${value.fieldValue} kr.`;

    return valueStr;
};

AmountOrUnknown.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const valueObject = getValue(formConfig, formValues, elementId);
    if (valueObject !== undefined) {
        return valueObject.isChecked ? true : valueObject.fieldValue !== '' ? true : false;
    } else {
        return false;
    }
};

export default AmountOrUnknown;
